import { SOCIAL_API_URL, ADMIN_API_URL, CHAT_API_URL, CLOUD_API_URL, MAILER_BASE_URL, API_BASE_URL } from '../config/apiBase';
import { createApiClient } from './axiosFactory';
import { getReferralCode } from '../utils/referral';

// Attach the captured referral code to a signup payload so the backend can
// attribute the new user to the channel/inviter that brought them in
// (api.py reads `referral_code` → DistributionService.use_referral_code +
// the `signup` channel event). Capture happens app-wide in useReferral, but
// no signup call was sending it, so every referred signup was landing as
// `direct`. An explicit code in `data` wins; an empty capture is omitted so
// the backend's `data.get('referral_code','')` stays falsy and is skipped.
const withReferral = (data) => {
  const code = getReferralCode();
  return code ? { referral_code: code, ...data } : data;
};

const socialApi = createApiClient(SOCIAL_API_URL);

// Hive contest endpoints live at /api/hive/contest/*, not /api/social/*
const hiveContestClient = createApiClient(`${API_BASE_URL}/api/hive/contest`);

// Admin API — does NOT nuke auth session on 401 (admin 401 ≠ token expiry)
const adminApiClient = createApiClient(ADMIN_API_URL, { handle401: false });

// --- Auth ---
export const authApi = {
  login: (data) => socialApi.post('/auth/login', data),
  register: (data) => socialApi.post('/auth/register', withReferral(data)),
  me: () => socialApi.get('/auth/me'),
  logout: () => socialApi.post('/auth/logout'),
  guestRegister: (data) => socialApi.post('/auth/guest-register', withReferral(data)),
  guestRecover: (data) => socialApi.post('/auth/guest-recover', data),
};

// --- Posts ---
export const postsApi = {
  list: (params) => socialApi.get('/posts', { params }),
  create: (data) => socialApi.post('/posts', data),
  get: (id) => socialApi.get(`/posts/${id}`),
  update: (id, data) => socialApi.patch(`/posts/${id}`, data),
  delete: (id) => socialApi.delete(`/posts/${id}`),
  upvote: (id) => socialApi.post(`/posts/${id}/upvote`),
  downvote: (id) => socialApi.post(`/posts/${id}/downvote`),
  removeVote: (id) => socialApi.delete(`/posts/${id}/vote`),
  report: (id, data) => socialApi.post(`/posts/${id}/report`, data),
};

// --- Site pages (CMS: draft -> in_review -> published) ---
// Backed by integrations/social/api_pages.py in HARTOS. Publishing goes
// through these endpoints so a post never needs a frontend redeploy.
export const pagesApi = {
  list: (status) => socialApi.get('/pages', {params: status ? {status} : {}}),
  get: (slug) => socialApi.get(`/pages/${slug}`),
  upsert: (data) => socialApi.post('/pages', data),
  setStatus: (slug, status) => socialApi.post(`/pages/${slug}/status`, {status}),
};

// --- Comments ---
export const commentsApi = {
  getByPost: (postId, params) => socialApi.get(`/posts/${postId}/comments`, { params }),
  create: (postId, data) => socialApi.post(`/posts/${postId}/comments`, data),
  reply: (commentId, data) => socialApi.post(`/comments/${commentId}/reply`, data),
  update: (commentId, data) => socialApi.patch(`/comments/${commentId}`, data),
  delete: (commentId) => socialApi.delete(`/comments/${commentId}`),
  upvote: (id) => socialApi.post(`/comments/${id}/upvote`),
  downvote: (id) => socialApi.post(`/comments/${id}/downvote`),
};

// --- Feed ---
export const feedApi = {
  personalized: (params) => socialApi.get('/feed', { params }),
  global: (params) => socialApi.get('/feed/all', { params }),
  trending: (params) => socialApi.get('/feed/trending', { params }),
  agents: (params) => socialApi.get('/feed/agents', { params }),
  agentSpotlight: () => socialApi.get('/feed/agent-spotlight'),
};

// --- Users ---
export const usersApi = {
  list: (params) => socialApi.get('/users', { params }),
  get: (id) => socialApi.get(`/users/${id}`),
  update: (id, data) => socialApi.patch(`/users/${id}`, data),
  posts: (id, params) => socialApi.get(`/users/${id}/posts`, { params }),
  comments: (id, params) => socialApi.get(`/users/${id}/comments`, { params }),
  karma: (id) => socialApi.get(`/users/${id}/karma`),
  follow: (id) => socialApi.post(`/users/${id}/follow`),
  unfollow: (id) => socialApi.delete(`/users/${id}/follow`),
  followers: (id, params) => socialApi.get(`/users/${id}/followers`, { params }),
  following: (id, params) => socialApi.get(`/users/${id}/following`, { params }),
  getAgents: (userId) => socialApi.get(`/users/${userId}/agents`),
};

// --- Communities ---
export const communitiesApi = {
  list: (params) => socialApi.get('/communities', { params }),
  create: (data) => socialApi.post('/communities', data),
  get: (id) => socialApi.get(`/communities/${id}`),
  posts: (id, params) => socialApi.get(`/communities/${id}/posts`, { params }),
  join: (id) => socialApi.post(`/communities/${id}/join`),
  leave: (id) => socialApi.delete(`/communities/${id}/leave`),
  members: (id, params) => socialApi.get(`/communities/${id}/members`, { params }),
};

// --- Search ---
export const searchApi = {
  search: (params) => socialApi.get('/search', { params }),
};

// --- Notifications ---
export const notificationsApi = {
  list: (params) => socialApi.get('/notifications', { params }),
  markRead: (ids) => socialApi.post('/notifications/read', { ids }),
  markAllRead: () => socialApi.post('/notifications/read-all'),
};

// --- Tasks ---
export const tasksApi = {
  create: (data) => socialApi.post('/tasks', data),
  list: (params) => socialApi.get('/tasks', { params }),
  get: (id) => socialApi.get(`/tasks/${id}`),
  assign: (id, data) => socialApi.post(`/tasks/${id}/assign`, data),
  complete: (id, data) => socialApi.post(`/tasks/${id}/complete`, data),
  mine: (params) => socialApi.get('/tasks', { params: { ...params, mine: true } }),
  myAgentsTasks: (params) => socialApi.get('/tasks', { params: { ...params, my_agents: true } }),
};

// --- Recipes ---
export const recipesApi = {
  list: (params) => socialApi.get('/recipes', { params }),
  get: (id) => socialApi.get(`/recipes/${id}`),
  share: (data) => socialApi.post('/recipes/share', data),
  fork: (id) => socialApi.post(`/recipes/${id}/fork`),
};

// --- Resonance ---
export const resonanceApi = {
  getWallet: () => socialApi.get('/resonance/wallet'),
  getWalletFor: (userId) => socialApi.get(`/resonance/wallet/${userId}`),
  getTransactions: (params) => socialApi.get('/resonance/transactions', { params }),
  getLeaderboard: (params) => socialApi.get('/resonance/leaderboard', { params }),
  dailyCheckin: () => socialApi.post('/resonance/daily-checkin'),
  getStreak: () => socialApi.get('/resonance/streak'),
  getBreakdown: (userId) => socialApi.get(`/resonance/breakdown/${userId}`),
  getLevelInfo: () => socialApi.get('/resonance/level-info'),
  boost: (data) => socialApi.post('/resonance/boost', data),
  getBoosts: (targetType, targetId) => socialApi.get(`/resonance/boosts/${targetType}/${targetId}`),
};

// --- Achievements ---
export const achievementsApi = {
  list: () => socialApi.get('/achievements'),
  getForUser: (userId) => socialApi.get(`/achievements/${userId}`),
  showcase: (achievementId, data) => socialApi.post(`/achievements/${achievementId}/showcase`, data),
};

// --- Challenges ---
export const challengesApi = {
  list: (params) => socialApi.get('/challenges', { params }),
  get: (id) => socialApi.get(`/challenges/${id}`),
  updateProgress: (id, data) => socialApi.post(`/challenges/${id}/progress`, data),
  claim: (id) => socialApi.post(`/challenges/${id}/claim`),
};

// --- Seasons ---
export const seasonsApi = {
  current: () => socialApi.get('/seasons/current'),
  leaderboard: (id, params) => socialApi.get(`/seasons/${id}/leaderboard`, { params }),
  achievements: (id) => socialApi.get(`/seasons/${id}/achievements`),
};

// --- Regions ---
export const regionsApi = {
  list: (params) => socialApi.get('/regions', { params }),
  get: (id) => socialApi.get(`/regions/${id}`),
  create: (data) => socialApi.post('/regions', data),
  update: (id, data) => socialApi.patch(`/regions/${id}`, data),
  join: (id) => socialApi.post(`/regions/${id}/join`),
  leave: (id) => socialApi.delete(`/regions/${id}/leave`),
  members: (id, params) => socialApi.get(`/regions/${id}/members`, { params }),
  feed: (id, params) => socialApi.get(`/regions/${id}/feed`, { params }),
  leaderboard: (id, params) => socialApi.get(`/regions/${id}/leaderboard`, { params }),
  governance: (id) => socialApi.get(`/regions/${id}/governance`),
  proposalVote: (regionId, proposalId, data) => socialApi.post(`/regions/${regionId}/governance/proposals/${proposalId}/vote`, data),
  promote: (id, data) => socialApi.post(`/regions/${id}/promote`, data),
  nearby: (params) => socialApi.get('/regions/nearby', { params }),
  sync: (id) => socialApi.post(`/regions/${id}/sync`),
};

// --- Encounters ---
export const encountersApi = {
  list: (params) => socialApi.get('/encounters', { params }),
  getWith: (userId) => socialApi.get(`/encounters/${userId}`),
  acknowledge: (id) => socialApi.post(`/encounters/${id}/acknowledge`),
  suggestions: () => socialApi.get('/encounters/suggestions'),
  bonds: () => socialApi.get('/encounters/bonds'),
  nearby: () => socialApi.get('/encounters/nearby'),
  // Proximity
  locationPing: (lat, lon, accuracy) => socialApi.post('/encounters/location-ping', {lat, lon, accuracy}),
  nearbyCount: () => socialApi.get('/encounters/nearby-now'),
  proximityMatches: (params) => socialApi.get('/encounters/proximity-matches', {params}),
  revealMatch: (matchId) => socialApi.post(`/encounters/proximity/${matchId}/reveal`),
  getLocationSettings: () => socialApi.get('/encounters/location-settings'),
  updateLocationSettings: (data) => socialApi.patch('/encounters/location-settings', data),
  // Missed connections
  createMissed: (data) => socialApi.post('/encounters/missed-connections', data),
  searchMissed: (params) => socialApi.get('/encounters/missed-connections', {params}),
  myMissed: (params) => socialApi.get('/encounters/missed-connections/mine', {params}),
  getMissed: (id) => socialApi.get(`/encounters/missed-connections/${id}`),
  respondMissed: (id, message) => socialApi.post(`/encounters/missed-connections/${id}/respond`, {message}),
  acceptMissedResponse: (id, responseId) => socialApi.post(`/encounters/missed-connections/${id}/accept/${responseId}`),
  deleteMissed: (id) => socialApi.delete(`/encounters/missed-connections/${id}`),
  suggestLocations: (lat, lon) => socialApi.get('/encounters/missed-connections/suggest-locations', {params: {lat, lon}}),
};

// --- Agent Evolution ---
export const evolutionApi = {
  get: (agentId) => socialApi.get(`/agents/${agentId}/evolution`),
  specialize: (agentId, data) => socialApi.post(`/agents/${agentId}/specialize`, data),
  leaderboard: (params) => socialApi.get('/agents/leaderboard', { params }),
  trees: () => socialApi.get('/agents/specialization-trees'),
  collaborations: (agentId, params) => socialApi.get(`/agents/${agentId}/collaborations`, { params }),
  collaborate: (agentId, data) => socialApi.post(`/agents/${agentId}/collaborate`, data),
  showcase: (params) => socialApi.get('/agents/showcase', { params }),
  history: (agentId) => socialApi.get(`/agents/${agentId}/evolution-history`),
};

// --- Ratings ---
export const ratingsApi = {
  submit: (data) => socialApi.post('/ratings', data),
  get: (userId) => socialApi.get(`/ratings/${userId}`),
  received: (userId, params) => socialApi.get(`/ratings/${userId}/received`, { params }),
  given: (userId, params) => socialApi.get(`/ratings/${userId}/given`, { params }),
  trust: (userId) => socialApi.get(`/trust/${userId}`),
};

// --- Referrals ---
export const referralsApi = {
  getCode: () => socialApi.get('/referral/code'),
  use: (data) => socialApi.post('/referral/use', data),
  stats: () => socialApi.get('/referral/stats'),
};

// --- Marketing attribution (channel funnel) ---
// Single client for the per-channel funnel WRITE: POST /api/social/marketing/track
// records by_code {click,download,install,signup}.  The flywheel posts ref-tagged
// links (?ref=<channel>); useReferral + the Download page call this so each
// channel's contribution to growth is countable.
// (Reading /marketing/stats is owned by the standalone InstallCounter component,
// which is intentionally service-layer-free — no stats() here to avoid a second
// path to the same endpoint.)
export const marketingApi = {
  track: (code, event, platform = 'web') =>
    socialApi.post('/marketing/track', { code, event, platform }),
  // READ the aggregated growth funnel: total_users vs target, new_users_7d,
  // and the per-channel {click,download,signup,signup_rate}. Powers the admin
  // Growth panel. Public endpoint (no auth), same gateway as the writer above.
  growth: () => socialApi.get('/marketing/growth'),
};

// --- Onboarding ---
export const onboardingApi = {
  getProgress: () => socialApi.get('/onboarding/progress'),
  completeStep: (data) => socialApi.post('/onboarding/complete-step', data),
  dismiss: () => socialApi.post('/onboarding/dismiss'),
  suggestion: () => socialApi.get('/onboarding/suggestion'),
};

// --- Campaigns ---
export const campaignsApi = {
  list: (params) => socialApi.get('/campaigns', { params }),
  get: (id) => socialApi.get(`/campaigns/${id}`),
  create: (data) => socialApi.post('/campaigns', data),
  update: (id, data) => socialApi.patch(`/campaigns/${id}`, data),
  delete: (id) => socialApi.delete(`/campaigns/${id}`),
  generateStrategy: (id) => socialApi.post(`/campaigns/${id}/generate-strategy`),
  executeStep: (id) => socialApi.post(`/campaigns/${id}/execute-step`),
  leaderboard: (params) => socialApi.get('/campaigns/leaderboard', { params }),
};

// --- Sharing (short URLs, OG, consent) ---
export const shareApi = {
  createLink: (resourceType, resourceId, isPrivate = false) =>
    socialApi.post('/share/link', { resource_type: resourceType, resource_id: resourceId, is_private: isPrivate }),
  resolve: (token) => socialApi.get(`/share/${token}`),
  trackView: (token) => socialApi.post(`/share/${token}/view`).catch(() => {}),
  checkConsent: (token) => socialApi.get(`/share/${token}/check-consent`),
  grantConsent: (token) => socialApi.post(`/share/${token}/consent`),
  stats: () => socialApi.get('/share/stats'),
};

// --- RSS/Atom Feeds ---
export const feedsApi = {
  // Get feed URLs (for sharing)
  getRssUrl: (type = 'global') =>
    `${SOCIAL_API_URL}/feeds/rss?type=${type}`,
  getAtomUrl: (type = 'global') =>
    `${SOCIAL_API_URL}/feeds/atom?type=${type}`,
  getJsonUrl: (type = 'global') =>
    `${SOCIAL_API_URL}/feeds/json?type=${type}`,
  getUserRssUrl: (userId) =>
    `${SOCIAL_API_URL}/users/${userId}/feed.rss`,
  getCommunityRssUrl: (communityId) =>
    `${SOCIAL_API_URL}/communities/${communityId}/feed.rss`,

  // Preview external feed
  preview: (url) => socialApi.post('/feeds/preview', { url }),

  // Import from external feed
  import: (url, communityId, limit = 10) =>
    socialApi.post('/feeds/import', { url, community_id: communityId, limit }),

  // Subscribe to external feed
  subscribe: (url, communityId, autoImport = true) =>
    socialApi.post('/feeds/subscribe', {
      url,
      community_id: communityId,
      auto_import: autoImport,
    }),
};

// --- Agent API (handle checking for guest login) ---
export const agentApi = {
  // Check if guest handle is available
  checkHandle: (handle) => socialApi.get('/handles/check', { params: { handle } })
    .catch(() => ({ available: true })), // Default to available if endpoint not found

  // Get agent by handle
  getByHandle: (handle) => socialApi.get(`/agents/by-handle/${handle}`),
};

// --- Admin API ---
export const adminApi = {
  // Dashboard stats (social_bp)
  stats: () => socialApi.get('/admin/stats'),

  // User management (social_bp)
  users: (params) => socialApi.get('/admin/users', { params }),
  updateUser: (userId, data) => socialApi.patch(`/admin/users/${userId}`, data),
  banUser: (userId, data) => socialApi.post(`/admin/users/${userId}/ban`, data),
  unbanUser: (userId) => socialApi.delete(`/admin/users/${userId}/ban`),

  // Agent sync (social_bp)
  syncAgents: () => socialApi.post('/admin/agents/sync'),

  // System metrics (channels admin_bp at /api/admin)
  metrics: () => adminApiClient.get('/metrics'),
  status: () => adminApiClient.get('/status'),
  latency: () => adminApiClient.get('/metrics/latency'),

  // Logs (social_bp)
  logs: (params) => socialApi.get('/admin/logs', { params }),

  // Revenue & Usage Analytics (central admin)
  revenueAnalytics: (params) => socialApi.get('/admin/revenue-analytics', { params }),
};

// --- Moderation API ---
export const moderationApi = {
  // Get reported content
  reports: (params) => socialApi.get('/admin/moderation/reports', { params }),
  getReport: (id) => socialApi.get(`/admin/moderation/reports/${id}`),
  resolveReport: (id, data) => socialApi.post(`/admin/moderation/reports/${id}/resolve`, data),

  // Content actions
  hidePost: (postId) => socialApi.post(`/admin/moderation/posts/${postId}/hide`),
  unhidePost: (postId) => socialApi.delete(`/admin/moderation/posts/${postId}/hide`),
  deletePost: (postId) => socialApi.delete(`/admin/moderation/posts/${postId}`),

  // Comment actions
  hideComment: (commentId) => socialApi.post(`/admin/moderation/comments/${commentId}/hide`),
  deleteComment: (commentId) => socialApi.delete(`/admin/moderation/comments/${commentId}`),
};

// --- Channels API (Bot integrations) --- uses channels admin_bp at /api/admin
export const channelsApi = {
  list: () => adminApiClient.get('/channels'),
  get: (channelType) => adminApiClient.get(`/channels/${channelType}`),
  create: (data) => adminApiClient.post('/channels', data),
  update: (channelType, data) => adminApiClient.put(`/channels/${channelType}`, data),
  delete: (channelType) => adminApiClient.delete(`/channels/${channelType}`),
  enable: (channelType) => adminApiClient.post(`/channels/${channelType}/enable`),
  disable: (channelType) => adminApiClient.post(`/channels/${channelType}/disable`),
  test: (channelType) => adminApiClient.post(`/channels/${channelType}/test`),
  reconnect: (channelType) => adminApiClient.post(`/channels/${channelType}/reconnect`),
};

// --- Channel User API (user-facing channel bindings) --- uses social_bp at /api/social
export const channelUserApi = {
  catalog: () => socialApi.get('/channels/catalog'),
  catalogChannel: (type) => socialApi.get(`/channels/catalog/${type}`),
  bindings: () => socialApi.get('/channels/bindings'),
  createBinding: (data) => socialApi.post('/channels/bindings', data),
  removeBinding: (id) => socialApi.delete(`/channels/bindings/${id}`),
  setPreferred: (id) => socialApi.put(`/channels/bindings/${id}/preferred`),
  generatePairCode: () => socialApi.post('/channels/pair/generate'),
  verifyPairCode: (data) => socialApi.post('/channels/pair/verify', data),
  presence: () => socialApi.get('/channels/presence'),
  conversations: (params) => socialApi.get('/channels/conversations', { params }),
};

// --- Workflows API --- uses channels admin_bp at /api/admin/automation/workflows
export const workflowsApi = {
  list: (params) => adminApiClient.get('/automation/workflows', { params }),
  get: (id) => adminApiClient.get(`/automation/workflows/${id}`),
  create: (data) => adminApiClient.post('/automation/workflows', data),
  update: (id, data) => adminApiClient.put(`/automation/workflows/${id}`, data),
  delete: (id) => adminApiClient.delete(`/automation/workflows/${id}`),
  enable: (id) => adminApiClient.post(`/automation/workflows/${id}/enable`),
  disable: (id) => adminApiClient.post(`/automation/workflows/${id}/disable`),
  test: (id, data) => adminApiClient.post(`/automation/workflows/${id}/execute`, data),
};

// --- Settings API --- uses channels admin_bp at /api/admin/config
export const settingsApi = {
  get: () => adminApiClient.get('/config'),
  update: (data) => adminApiClient.put('/config', data),
  getSecurity: () => adminApiClient.get('/config/security'),
  updateSecurity: (data) => adminApiClient.put('/config/security', data),
  getMedia: () => adminApiClient.get('/config/media'),
  updateMedia: (data) => adminApiClient.put('/config/media', data),
  getResponse: () => adminApiClient.get('/config/response'),
  updateResponse: (data) => adminApiClient.put('/config/response', data),
  getMemory: () => adminApiClient.get('/config/memory'),
  updateMemory: (data) => adminApiClient.put('/config/memory', data),
  getEmbodiedAI: () => adminApiClient.get('/config/embodied'),
  updateEmbodiedAI: (data) => adminApiClient.put('/config/embodied', data),
  toggleFeed: (data) => adminApiClient.post('/config/embodied/toggle', data),
  getEmbodiedStatus: () => adminApiClient.get('/config/embodied/status'),
  export: () => adminApiClient.get('/config/export'),
  import: (data) => adminApiClient.post('/config/import', data),
  reset: () => adminApiClient.post('/config/reset'),
};

// --- Identity API (Agent personality) --- uses channels admin_bp at /api/admin/identity
export const identityApi = {
  get: () => adminApiClient.get('/identity'),
  update: (data) => adminApiClient.put('/identity', data),
  getAvatars: () => adminApiClient.get('/identity/avatars'),
  uploadAvatar: (data) => adminApiClient.post('/identity/avatars', data),
  deleteAvatar: (id) => adminApiClient.delete(`/identity/avatars/${id}`),
  setDefaultAvatar: (id) => adminApiClient.post(`/identity/avatars/${id}/default`),
  getSenderMappings: () => adminApiClient.get('/identity/sender-mappings'),
  createSenderMapping: (data) => adminApiClient.post('/identity/sender-mappings', data),
  deleteSenderMapping: (id) => adminApiClient.delete(`/identity/sender-mappings/${id}`),
};

// --- Agent Dashboard API (truth-grounded, auto-refresh) ---
export const dashboardApi = {
  agents: () => socialApi.get('/dashboard/agents'),
  health: () => socialApi.get('/dashboard/health'),
};

// --- Chat API (Local Nunba backend) ---
// Local LLM inference can take 60-90s on small models — use 120s timeout
const chatApiClient = createApiClient(CHAT_API_URL, { timeout: 120000, cache: false });

// Cloud fallback client (no 401 handling — public endpoints)
const cloudApiClient = createApiClient(CLOUD_API_URL, { handle401: false, cache: false });

// --- Mailer API (external service — no JWT auth, no 401 handling) ---
const mailerApiClient = createApiClient(MAILER_BASE_URL, { handle401: false, cache: false });

export const chatApi = {
  // Get prompts/agents (local and cloud)
  getPrompts: (userId) => chatApiClient.get('/prompts', { params: { user_id: userId } }),

  // Get ALL public prompts (local-first, includes cloud merge)
  getPublicPrompts: () => chatApiClient.get('/prompts/public'),

  // The CURATED catalogue: the built-in agents a backend chooses to present,
  // each with avatar/description/capabilities/type. routes/chatbot_routes.py
  // reads ?type= and answers 'all' | 'local' | 'cloud'. A local Nunba serves
  // its built-ins here (Hevolve, HART Coder, HART Writer, ...); /prompts/public
  // does not exist there, which is why the gallery used to fall through to the
  // raw dump and show storage rows instead.
  getCatalogPrompts: (type = 'all') =>
    chatApiClient.get('/prompts', { params: { type } }),

  // Cloud fallbacks (only when local backend is unreachable)
  getPublicPromptsCloud: () => mailerApiClient.get('/getprompt_all/'),
  getUserPromptsCloud: (userId) => mailerApiClient.get('/getprompt_userid/', { params: { user_id: userId } }),

  // Chat with agent
  chat: (data) => chatApiClient.post('/chat', data),

  // Custom GPT endpoint
  customGpt: (data) => chatApiClient.post('/custom_gpt', data),

  // TTS endpoints
  ttsVoices: () => chatApiClient.get('/tts/voices'),
  ttsSynthesize: (data) => chatApiClient.post('/tts/synthesize', data),
  ttsStatus: () => chatApiClient.get('/tts/status'),

  // Health check
  health: () => chatApiClient.get('/backend/health'),

  // Network status
  networkStatus: () => chatApiClient.get('/network/status'),

  // Agent sync (multi-device)
  getAgentSync: () => chatApiClient.get('/agents/sync'),
  syncAgents: (agents) => chatApiClient.post('/agents/sync', { agents }),

  // Agent migration (guest → authenticated)
  migrateAgents: (data) => chatApiClient.post('/agents/migrate', data),

  // LLM config (AI provider management)
  getLlmConfig: () => chatApiClient.get('/api/llm/config'),
  updateLlmConfig: (data) => chatApiClient.post('/api/llm/config', data),
  testLlmConnection: (data) => chatApiClient.post('/api/llm/test', data),

  // Vault API (tool keys + channel secrets — encrypted, machine-locked)
  vaultStore: (data) => chatApiClient.post('/api/vault/store', data),
  vaultKeys: () => chatApiClient.get('/api/vault/keys'),
  vaultHas: (keyName, channelType) =>
    chatApiClient.get('/api/vault/has', { params: { key_name: keyName, channel_type: channelType || '' } }),

  // Generic passthrough (for one-off endpoints)
  get: (path, config) => chatApiClient.get(path, config),
  post: (path, data, config) => chatApiClient.post(path, data, config),
};

// --- Thought Experiment Tracker API ---
export const trackerApi = {
  listExperiments: (params) => socialApi.get('/tracker/experiments', { params }),
  getExperiment: (postId) => socialApi.get(`/tracker/experiments/${postId}`),
  getConversations: (postId) => socialApi.get(`/tracker/experiments/${postId}/conversations`),
  approve: (postId, data) => socialApi.post(`/tracker/experiments/${postId}/approve`, data),
  reject: (postId, data) => socialApi.post(`/tracker/experiments/${postId}/reject`, data),
  getNotifications: () => socialApi.get('/tracker/notifications'),
  // Pledge endpoints (canonical — all pledge ops go through tracker_bp)
  pledges: (postId) => socialApi.get(`/tracker/experiments/${postId}/pledges`),
  pledgeSummary: (postId) => socialApi.get(`/tracker/experiments/${postId}/pledge-summary`),
  pledge: (postId, data) => socialApi.post(`/tracker/experiments/${postId}/pledge`, data),
  withdrawPledge: (postId, escrowId) => socialApi.delete(`/tracker/experiments/${postId}/pledge/${escrowId}`),
  insights: (postId) => socialApi.get(`/tracker/experiments/${postId}/insights`),
  myPledges: (params) => socialApi.get('/tracker/pledges/mine', { params }),
  allPledges: (params) => socialApi.get('/tracker/pledges/all', { params }),
  verifyPledge: (escrowId) => socialApi.post(`/tracker/pledges/${escrowId}/verify`),
  // Hive View endpoints (injection, interview, dual-context, encounters)
  inject: (postId, data) => socialApi.post(`/tracker/experiments/${postId}/inject`, data),
  interview: (postId, data) => socialApi.post(`/tracker/experiments/${postId}/interview`, data),
  dualContext: (data) => socialApi.post('/tracker/dual-context', data),
  encounters: () => socialApi.get('/tracker/encounters'),
};

// --- Experiment Discovery ---
export const experimentsApi = {
  discover: (params) => socialApi.get('/experiments/discover', { params }),
  list: (params) => socialApi.get('/experiments', { params }),
  get: (id) => socialApi.get(`/experiments/${id}`),
  create: (data) => socialApi.post('/experiments', data),
  vote: (id, data) => socialApi.post(`/experiments/${id}/vote`, data),
  advance: (id, data) => socialApi.post(`/experiments/${id}/advance`, data),
  evaluate: (id) => socialApi.post(`/experiments/${id}/evaluate`),
  decide: (id, data) => socialApi.post(`/experiments/${id}/decide`, data),
  votes: (id) => socialApi.get(`/experiments/${id}/votes`),
  timeline: (id) => socialApi.get(`/experiments/${id}/timeline`),
  coreIp: () => socialApi.get('/experiments/core-ip'),
  metrics: (id) => socialApi.get(`/experiments/${id}/metrics`),
  contribute: (id, data) => socialApi.post(`/experiments/${id}/contribute`, data),
};

// --- Sync & Backup ---
export const syncApi = {
  createBackup: (data) => socialApi.post('/sync/backup', data),
  getBackupMetadata: () => socialApi.get('/sync/backup/metadata'),
  restore: (data) => socialApi.post('/sync/restore', data),
  linkDevice: (data) => socialApi.post('/sync/link-device', data),
  listDevices: () => socialApi.get('/sync/devices'),
  unlinkDevice: (id) => socialApi.delete(`/sync/devices/${id}`),
};

// --- Audit ---
export const auditApi = {
  listAgents: (params) => socialApi.get('/audit/agents', { params }),
  getTimeline: (agentId, params) => socialApi.get(`/audit/agents/${agentId}/timeline`, { params }),
  getConversations: (agentId) => socialApi.get(`/audit/agents/${agentId}/conversations`),
  getThinking: (agentId) => socialApi.get(`/audit/agents/${agentId}/thinking`),
  getDaemonActivity: () => socialApi.get('/audit/daemon/activity'),
  getDaemonGoals: (params) => socialApi.get('/audit/daemon/goals', { params }),
  getComputeNodes: () => socialApi.get('/audit/compute/nodes'),
  getComputeUsage: (params) => socialApi.get('/audit/compute/usage', { params }),
  getComputeRouting: () => socialApi.get('/audit/compute/routing'),
};

// --- Ads ---
export const adApi = {
  serve: (placement, params = {}) => socialApi.get('/ads/serve', { params: { placement_name: placement, ...params } }),
  impression: (adId) => socialApi.post(`/ads/${adId}/impression`).catch(() => {}),
  click: (adId) => socialApi.post(`/ads/${adId}/click`).catch(() => {}),
};

// --- Content Generation Tracking ---
export const contentGenApi = {
  getGames: () => socialApi.get('/content-gen/games'),
  getGame: (gameId) => socialApi.get(`/content-gen/games/${gameId}`),
  getStuck: (thresholdHours = 24) => socialApi.get('/content-gen/stuck', { params: { threshold_hours: thresholdHours } }),
  retry: (gameId, taskType = null) => socialApi.post('/content-gen/retry', { game_id: gameId, task_type: taskType }),
  getServices: () => socialApi.get('/content-gen/services'),
  register: (gameId, gameConfig) => socialApi.post('/content-gen/register', { game_id: gameId, game_config: gameConfig }),
};

// --- Theme (OS-wide appearance) ---
export const themeApi = {
  getPresets: () => socialApi.get('/theme/presets'),
  getActive: () => socialApi.get('/theme/active'),
  apply: (themeId) => socialApi.post('/theme/apply', { theme_id: themeId }),
  customize: (overrides) => socialApi.post('/theme/customize', overrides),
  getFonts: () => socialApi.get('/theme/fonts'),
  generate: (description, basePreset) =>
    socialApi.post('/theme/generate', { description, base_preset: basePreset }),
  getUserTheme: (userId) => socialApi.get(`/users/${userId}/theme`),
};

export const mailerApi = {
  // OTP
  sendOtp: (data) => mailerApiClient.post('/send_otp', data),
  validateOtp: (data) => mailerApiClient.post('/validate_otp', data),
  verifyOtp: (data) => mailerApiClient.post('/varify_otp', data),
  renewToken: (data) => mailerApiClient.post('/refresh_tokens', data),

  // Teacher/Admin auth
  verifyTeacher: (data) => mailerApiClient.post('/verifyTeacher', data),
  verifyTeacherByPhone: (data) => mailerApiClient.post('/verifyTeacherByPhone', data),
  registerTeacher: (data) => mailerApiClient.post('/register_teacher', data),
  registerStudent: (data) => mailerApiClient.post('/register_student', data),
  createClient: (data) => mailerApiClient.post('/createclient', data),
  allClients: () => mailerApiClient.get('/allclients'),
  deleteUser: (data) => mailerApiClient.post('/delete_user_by_email_or_phone_num', data),
  confirmDeleteUser: (data) => mailerApiClient.post('/confirm_delete_user_by_email_or_phone_num', data),

  // Courses/Content
  getSubjects: () => mailerApiClient.get('/getuniquesubject'),
  getStandards: () => mailerApiClient.get('/getstandard'),
  getBoards: () => mailerApiClient.get('/getboard'),
  getBooks: () => mailerApiClient.get('/getbooks'),
  getCourses: () => mailerApiClient.get('/getuniquecourse'),
  getBatch: () => mailerApiClient.get('/getbatch'),
  createCourse: (data) => mailerApiClient.post('/create_course', data),
  createBookSubject: (data) => mailerApiClient.post('/createbooksubject', data),
  getBooksByCourse: (courseId) => mailerApiClient.get(`/getbooksbycourse?course_name=${courseId}`),

  // Subscriptions/Payments
  getPlans: () => mailerApiClient.get('/getallplandetails'),
  addSubscription: (data) => mailerApiClient.post('/addsubscription_by_phone', data),
  makePayment: (data) => mailerApiClient.post('/makepayment', data),
  deductCredits: (data) => mailerApiClient.post('/deduct-credits', data),

  // Assessments
  allAssessments: (params) => mailerApiClient.get('/allassessments', { params }),
  getQAByAssessment: (name) => mailerApiClient.get('/get_all_QAs_by_assessment_name', { params: { assessment_name: name } }),
  createPromptList: (data) => mailerApiClient.post('/createpromptlist', data),
  updateQA: (id, data) => mailerApiClient.put(`/updateQA/${id}`, data),
  deleteQA: (id) => mailerApiClient.delete(`/deleteQA/${id}`),

  // Prompts
  getPromptsByUser: (userId) => mailerApiClient.get(`/getprompt_onlyuserid/?user_id=${userId}`),
  getFamousCharacters: () => mailerApiClient.get('/get_famous_character'),

  // Generic request (for edge cases during migration)
  get: (path, config) => mailerApiClient.get(path, config),
  post: (path, data, config) => mailerApiClient.post(path, data, config),
};

// ─── Multiplayer Games ───
export const gamesApi = {
  catalog: (params) => socialApi.get('/games/catalog', { params }),
  create: (data) => socialApi.post('/games', data),
  list: (params) => socialApi.get('/games', { params }),
  get: (id) => socialApi.get(`/games/${id}`),
  join: (id) => socialApi.post(`/games/${id}/join`),
  ready: (id) => socialApi.post(`/games/${id}/ready`),
  start: (id) => socialApi.post(`/games/${id}/start`),
  move: (id, data) => socialApi.post(`/games/${id}/move`, data),
  leave: (id) => socialApi.post(`/games/${id}/leave`),
  results: (id) => socialApi.get(`/games/${id}/results`),
  history: (params) => socialApi.get('/games/history', { params }),
  quickMatch: (data) => socialApi.post('/games/quick-match', data),
  fromEncounter: (encounterId, data) => socialApi.post(`/games/from-encounter/${encounterId}`, data),
};

// ─── Compute Lending ───
export const computeApi = {
  optIn: () => socialApi.post('/compute/opt-in'),
  optOut: () => socialApi.post('/compute/opt-out'),
  status: () => socialApi.get('/compute/status'),
  impact: () => socialApi.get('/compute/impact'),
  communityImpact: () => socialApi.get('/compute/community-impact'),
  healthCheck: () => socialApi.post('/compute/health-check'),
};

// Compute Earnings (HARTOS /api/compute/earnings/*).  Distinct from
// /api/social/compute/* above: the earnings surface lives at the top
// of the API tree because it is a cross-cutting concern (settle path
// writes Spark; banner reads tier estimate; drawer streams settled
// rows).  Same axiosFactory client; just a different URL prefix.
const computeEarningsClient = createApiClient(`${API_BASE_URL}/api/compute/earnings`);
export const computeEarningsApi = {
  estimate: (params) => computeEarningsClient.get('/estimate', { params }),
  list: (uid, params) => computeEarningsClient.get(`/${uid}`, { params }),
  // SSE stream URL for EventSource consumers (matches hiveContestApi.ideasStreamUrl shape).
  streamUrl: () => `${API_BASE_URL}/api/compute/earnings/stream`,
};

// pledgeApi removed — all pledge endpoints consolidated into trackerApi above.
// Use trackerApi.pledge(), trackerApi.pledgeSummary(), trackerApi.insights(), etc.

// --- MCP Server Registry ---
export const mcpApi = {
  servers: (params) => socialApi.get('/mcp/servers', { params }),
  tools: (serverId) => socialApi.get(`/mcp/servers/${serverId}/tools`),
  register: (data) => socialApi.post('/mcp/register', data),
  discover: (params) => socialApi.get('/mcp/discover', { params }),
};

// --- Hive Contest ---
// Backend lives in HARTOS at /api/hive/contest/*; see
// HARTOS/integrations/social/api_hive_contest.py for the contract.
// Floating ideas wall on hevolve.ai subscribes to /ideas/stream via SSE.
export const hiveContestApi = {
  info: () => hiveContestClient.get('/info'),
  leaderboard: (params) => hiveContestClient.get('/leaderboard', { params }),
  ideas: (params) => hiveContestClient.get('/ideas', { params }),
  submitIdea: (data) => hiveContestClient.post('/ideas', data),
  join: (data) => hiveContestClient.post('/join', data),
  // SSE endpoint URL for EventSource (clients open it directly).
  ideasStreamUrl: () => `${API_BASE_URL}/api/hive/contest/ideas/stream`,
};

// --- Marketplace ---
export const marketplaceApi = {
  listings: (params) => socialApi.get('/marketplace/listings', { params }),
  get: (id) => socialApi.get(`/marketplace/listings/${id}`),
  create: (data) => socialApi.post('/marketplace/listings', data),
  hire: (id, data) => socialApi.post(`/marketplace/listings/${id}/hire`, data),
  reviews: (id) => socialApi.get(`/marketplace/listings/${id}/reviews`),
  addReview: (id, data) => socialApi.post(`/marketplace/listings/${id}/reviews`, data),
  categories: () => socialApi.get('/marketplace/categories'),
};

export default socialApi;
